import React from "react";
import Head from "next/head";
import Link from "next/link";

import { Prism as SyntaxHighlighter } from "react-syntax-highlighter";
import { FaWordpress } from "react-icons/fa";
import { IoIosArrowDropright } from "react-icons/io";

import { a11yDark } from "utils/react-syntax-highlighter";
import { createUTMQueryParams } from "@utils/signature_owner";

const WordpressIntegration = () => {
  return (
    <div className="max-w-screen-xl mx-auto mb-10 md:mt-20 mt-8 md:px-8 px-4">
      <Head>
        <meta charSet="utf-8" />
        <title>Wordpress Integration | BoloSign</title>
        <meta
          name="description"
          content="Electronic signatures refer to signatures in digital format, distinguishing them from traditional handwritten signatures. These digital signatures hold full legal validity for indicating consent or endorsement of document contents. The prevalence of eSignatures is progressively supplanting manual signatures, serving as a versatile method to sign a wide array of documents spanning various sectors on a global scale."
        />
        <meta name="geo.region" content="IN" />
        <meta name="geo.position" content="22.351115;78.667743" />
        <meta name="ICBM" content="22.351115, 78.667743" />
        <meta name="author" content="BoloSign" />
        <meta name="publisher" content="BoloSign" />
        <meta
          name="keywords"
          content="eSignature, electronic signature, digital signature, eSign, eSignatures, electronic signatures, digital signatures, eSignature software, electronic signature software, digital signature software, eSignature solution, electronic signature solution, digital signature solution, eSignature app, electronic signature app, digital signature app, eSignature platform, electronic signature platform, digital signature platform, eSignature service, electronic signature service, digital signature service, eSignature tool, electronic signature tool, digital signature tool, eSignature online, electronic signature online, digital signature online, eSignature solution online, electronic signature solution online, digital signature solution online, eSignature software online, electronic signature software online, digital signature software online, eSignature platform online, electronic signature platform online, digital signature platform online"
        />
        <meta
          property="og:url"
          content="https://www.bolosign.com/signature/wordpress-integration/"
        />
        <meta property="og:type" content="website" />
        <meta property="og:title" content="Wordpress Integration | BoloSign" />
        <meta name="twitter:site" content="@boloforms" />
        <meta
          property="twitter:url"
          content="https://www.bolosign.com/signature/wordpress-integration/"
        />
        <meta name="twitter:title" content="Wordpress Integration | BoloSign" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:creator" content="@boloforms" />
        <meta property="twitter:domain" content="https://bolosign.com" />
        <meta name="twitter:description" content="" />
        <meta property="og:description" content="" />
        <meta
          name="robots"
          content="INDEX, FOLLOW, MAX-IMAGE-PREVIEW:LARGE, MAX-SNIPPET:-1, MAX-VIDEO-PREVIEW:-1"
        />
        <link
          rel="canonical"
          href="https://www.bolosign.com/signature/wordpress-integration/"
        />
      </Head>

      <section>
        <div>
          <FaWordpress className="text-8xl mx-auto" />

          <h1 className="mt-5 text-center text-4xl font-bold sm:text-6xl">
            Document Signing for <br />
            <span className="text-[#21759b]">Wordpress</span>
          </h1>
        </div>
      </section>

      <section className="mt-10 mx-auto">
        <p className="text-lg sm:text-xl">
          WordPress stands as one of the most popular platforms for website
          creation. This versatile platform simplifies the process of building
          websites, adding content, and enhancing functionality with a variety
          of plugins. However, if your website needs to handle PDF document
          signings, BoloSign can be a game-changer. In just a few steps, you can
          integrate the capability to sign PDF documents directly on your site.
          This feature is incredibly useful for businesses that operate online
          or websites offering online services, streamlining processes and
          improving user experience.
        </p>

        <div className="mt-10">
          <h2 className="text-2xl sm:text-4xl mt-0 mb-6">
            How to Sign PDF in Wordpress
          </h2>

          <ul className="text-lg sm:text-xl leading-9 sm:leading-10 list-disc pl-10">
            <li>
              <Link
                className="underline"
                href={`https://signature.boloforms.com/register?from=WEBSITE_BOTTOM_BUTTON${createUTMQueryParams(
                  {
                    from: "WEBSITE_BOTTOM_BUTTON",
                  }
                )}`}
                target="_blank"
              >
                Sign up
              </Link>{" "}
              for a free BoloSign account and upload the PDF document you need
              signed.
            </li>
            <li>
              Open your uploaded PDF in the BoloSign editor and insert the
              necessary fields for signatures and any other required
              information.
            </li>
            <li>
              Copy the embed code provided by BoloSign and embed it on your
              website at the desired location.
            </li>
            <li>
              Start receiving signed copies of your PDF documents directly in
              your BoloSign account and via email.
            </li>
          </ul>
        </div>

        <div className="mt-10 space-y-8">
          <h3 className="flex items-center space-x-2 text-xl sm:text-3xl">
            <IoIosArrowDropright />

            <span>PDF Template Creation</span>
          </h3>

          <p className="text-lg sm:text-xl">
            Before diving in, start by{" "}
            <Link
              className="underline"
              href={`https://signature.boloforms.com/register?from=WEBSITE_BOTTOM_BUTTON${createUTMQueryParams(
                {
                  from: "WEBSITE_BOTTOM_BUTTON",
                }
              )}`}
              target="_blank"
            >
              creating a free account
            </Link>{" "}
            on our website and prepare the document you will use as a basis for
            your template.
          </p>

          <p className="text-lg sm:text-xl">
            On the PDF Templates page of BoloSign, click the{" "}
            <span className="font-bold">{`"Create Template"`}</span> button
            located in the top right corner. In the next page that appears,
            upload PDF and add roles. Choose these roles carefully, as they will
            be used to assign fields to the appropriate signers.
          </p>

          <p className="text-lg sm:text-xl">
            In the center of the editor page, you&apos;ll find a section where
            you can either drag and drop your document or click to select a file
            from your computer. Once the document is uploaded, it will appear on
            the screen, ready for you to start adding fields. You&apos;ll find
            all the possible fields you can add in the right-hand panel. Feel
            free to experiment with the placement and sizes of these fields to
            ensure the document looks exactly as you want it to. And remember,
            there&apos;s no need to worry - you can always modify the template
            after it&apos;s been created, so you have the flexibility to adjust
            things as needed.
          </p>

          <p className="text-lg sm:text-xl">
            After adding all the necessary fields and ensuring the document
            looks exactly as you want, click the{" "}
            <span className="font-bold">{`"Get Invite Link"`}</span> button.
            Your document will be saved in your account and you will get a link
            to invite signer to sign this document. You can use this link to
            create a iframe and embed it on your website.
          </p>

          <div>
            <SyntaxHighlighter
              language="html"
              style={a11yDark}
            >{`<iframe src="your_pdf_template_link" width= 200px" height=700px" name="BoloForms"></iframe>`}</SyntaxHighlighter>
          </div>

          <div className="md:h-[640px] h-auto">
            <iframe
              src="https://app.tango.us/app/embed/ce399adf-305b-42be-a9c1-03281b96ce01?skipCover=false&defaultListView=false&skipBranding=true"
              style={{ minHeight: "400px" }}
              sandbox="allow-scripts allow-top-navigation-by-user-activation allow-popups allow-same-origin"
              security="restricted"
              title="How to Create Pdf Templates and Get Html code to Embed using IFrame"
              width="100%"
              height="100%"
              referrerpolicy="strict-origin-when-cross-origin"
              frameborder="0"
              webkitallowfullscreen="webkitallowfullscreen"
              mozallowfullscreen="mozallowfullscreen"
              allowfullscreen="allowfullscreen"
            ></iframe>
          </div>
        </div>

        <div className="mt-10 space-y-8">
          <h3 className="flex items-center space-x-2 text-xl sm:text-3xl">
            <IoIosArrowDropright />

            <span>Form Creation</span>
          </h3>

          <p className="text-lg sm:text-xl">
            Make sure you have{" "}
            <Link
              className="underline"
              href={`https://signature.boloforms.com/register?from=WEBSITE_BOTTOM_BUTTON${createUTMQueryParams(
                {
                  from: "WEBSITE_BOTTOM_BUTTON",
                }
              )}`}
              target="_blank"
            >
              created a free account
            </Link>{" "}
            on our website and prepare the document you will use as a basis for
            your form.
          </p>

          <p className="text-lg sm:text-xl">
            On the Forms page of BoloSign, click the{" "}
            <span className="font-bold">{`"Create Form"`}</span> button located
            in the top right corner. In the next page that appears, you will
            have to add fields in the form. You can add text fields, signature
            fields, date fields, etc.
          </p>

          <p className="text-lg sm:text-xl">
            Click on the <span className="font-bold">{`"Customize PDF"`}</span>{" "}
            button on top right corner to upload your PDF document. Click on
            continue button to map fields in the PDF document.
          </p>

          <p className="text-lg sm:text-xl">
            In the right of the editor page, you&apos;ll find a section where
            you can click to select fields to add in the form. Feel free to
            experiment with the placement and sizes of these fields to ensure
            the document looks exactly as you want it to. And remember,
            there&apos;s no need to worry - you can always modify the template
            after it&apos;s been created, so you have the flexibility to adjust
            things as needed.
          </p>

          <p className="text-lg sm:text-xl">
            After adding all the necessary fields and ensuring the document
            looks exactly as you want, go back to the form editor by clicking on
            the form button top left of the screen. Here click the{" "}
            <span className="font-bold">{`"Send"`}</span> button. Now in the
            popup that appears, click on the{" "}
            <span className="font-bold">{`"< >"`}</span> button. You will get a
            code to embed the form on your website.
          </p>

          <div>
            <SyntaxHighlighter
              language="html"
              style={a11yDark}
            >{`<iframe src="your_form_link" width= 200px" height=700px" name="BoloForms"></iframe>`}</SyntaxHighlighter>
          </div>

          <div>
            <iframe
              src="https://app.tango.us/app/embed/f0336757-8f9d-474d-8850-6b51b635a2fb?skipCover=false&defaultListView=false&skipBranding=true"
              style={{ minHeight: "400px" }}
              sandbox="allow-scripts allow-top-navigation-by-user-activation allow-popups allow-same-origin"
              security="restricted"
              title="How to Create Form and Get Html Code to Embed"
              width="100%"
              height="100%"
              referrerpolicy="strict-origin-when-cross-origin"
              frameborder="0"
              webkitallowfullscreen="webkitallowfullscreen"
              mozallowfullscreen="mozallowfullscreen"
              allowfullscreen="allowfullscreen"
            ></iframe>
          </div>
        </div>

        <div className="mt-10 space-y-8">
          <h3 className="flex space-x-2 text-xl sm:text-3xl">
            <IoIosArrowDropright className="mt-1" />

            <span>Adding the Embed HTML Code to a WordPress Page</span>
          </h3>

          <p className="text-lg sm:text-xl">
            You can insert the signing form for your document template on any
            page of your website. Whether it&apos;s a contact page or a
            dedicated document signing page, the process is straightforward.
          </p>

          <p className="text-lg sm:text-xl">
            To embed the signing form, navigate to the admin panel of your
            WordPress website. Then, select the page where you want to place the
            signing form and click on{" "}
            <span className="font-bold">{`"Edit"`}</span>. Decide where on the
            page you&apos;d like the form to appear and click on the{" "}
            <span className="font-bold">{`"+"`}</span> button (Add block). In
            the search field, type{" "}
            <span className="font-bold">{`"Custom HTML"`}</span> if you&apos;re
            using the English version of WordPress, or the equivalent
            translation for other languages. In the block that appears, paste
            the HTML code you prepared earlier into the input field.
          </p>

          <p className="text-lg sm:text-xl">
            After you&apos;ve pasted the HTML code, click on the{" "}
            <span className="font-bold">{`"Preview"`}</span> button to see how
            the form will look on your website and then save the changes to the
            page. Now, the document signing form will be available on your
            website, allowing users to easily sign documents directly on your
            site.
          </p>
        </div>

        <div className="mt-10">
          <h3 className="text-xl sm:text-3xl">Conclusions</h3>

          <p className="mt-5 text-lg sm:text-xl">
            In this guide, we&apos;ve only scratched the surface of what&apos;s
            possible with BoloSign within a WordPress application. BoloSign
            offers various eSignature features that you can utilize to enhance
            your application. You have the ability to customize the signing form
            on your website to a great extent using various parameters, ensuring
            that it perfectly aligns with your site&apos;s style and functional
            needs. Importantly, leveraging these advanced features doesn&apos;t
            require the installation of additional plugins or extensions.
            Everything operates smoothly with WordPress&apos;s basic
            capabilities, making it a straightforward and efficient way to
            elevate your website&apos;s functionality and user experience.
          </p>
        </div>
      </section>
    </div>
  );
};

export default WordpressIntegration;
